/**
 * What an invitation draft needs before it is sent, and which field a server
 * refusal belongs to.
 *
 * @module components/admin/invite-draft
 */

import type { InviteRequest } from "@/components/admin/useTenantUsers";
import { parseAddressList } from "@/components/shared/address-list";
import type { FieldProblem } from "@/utils/field-problems";

export type InviteField = "email" | "alternateEmails";

/** The control id each field renders with, so a problem can be focused. */
export const INVITE_FIELD_IDS: Record<InviteField, string> = {
  email: "invite-email",
  alternateEmails: "invite-alternate-emails",
};

/** A shape check only; the directory decides whether the address is real. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function alternateProblem(
  address: string,
  primary: string,
  existing: ReadonlySet<string>,
): string | undefined {
  const normalised = address.toLowerCase();
  if (!EMAIL_SHAPE.test(address)) {
    return `${address} is not an email address.`;
  }
  if (normalised === primary) {
    return `${address} is already the main email.`;
  }
  if (existing.has(normalised)) {
    return `${address} already belongs to a user in this organisation.`;
  }
  return undefined;
}

/**
 * @param draft - The form as typed.
 * @param existingEmails - Addresses of the organisation's current users.
 * @returns Every problem, in the order the fields appear on screen.
 */
export function validateInvite(
  draft: InviteRequest,
  existingEmails: readonly string[],
): FieldProblem<InviteField>[] {
  const existing = new Set(existingEmails.map((email) => email.toLowerCase()));
  const problems: FieldProblem<InviteField>[] = [];
  const email = draft.email.trim();
  if (!email) {
    problems.push({ field: "email", message: "Enter an email address." });
  } else if (!EMAIL_SHAPE.test(email)) {
    problems.push({ field: "email", message: "Enter an email address like name@example.edu." });
  } else if (existing.has(email.toLowerCase())) {
    problems.push({
      field: "email",
      message: `${email} already belongs to a user in this organisation.`,
    });
  }
  const primary = email.toLowerCase();
  const alternate = parseAddressList(draft.alternateEmails)
    .map((address) => alternateProblem(address, primary, existing))
    .find((message) => message !== undefined);
  if (alternate) {
    problems.push({ field: "alternateEmails", message: alternate });
  }
  return problems;
}

/**
 * The field a server refusal is about, found by the address it names.
 *
 * @returns `undefined` when it names none of the draft's addresses, and the
 *   refusal then belongs to the form as a whole.
 */
export function fieldForRefusal(message: string, draft: InviteRequest): InviteField | undefined {
  const said = message.toLowerCase();
  const email = draft.email.trim().toLowerCase();
  if (email && said.includes(email)) {
    return "email";
  }
  return parseAddressList(draft.alternateEmails).some((address) =>
    said.includes(address.toLowerCase()),
  )
    ? "alternateEmails"
    : undefined;
}
